import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, IconButton, Skeleton, Typography } from '@mui/material';
import { MdArrowBack } from 'react-icons/md';
import { useNavigate, useParams } from 'react-router-dom';
import ProductForm from './ProductForm';
import { applyFormChange, authToken, buildProductFormData, FONT, formFromProduct, initialFormState, StatusChip, yellowBtn } from './productShared';
import { getData, putData } from '../../Webservice/webservice';

const EditProduct = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [form, setForm] = useState(initialFormState);
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [serverError, setServerError] = useState('');

    const fetchCategories = useCallback(async () => {
        try {
            const res = await getData('categories/list?limit=1000', null, authToken());
            setCategories(Array.isArray(res?.categories) ? res.categories : []);
        } catch { setCategories([]); }
    }, []);

    const fetchSubcategories = useCallback(async (categoryId) => {
        if (!categoryId) { setSubcategories([]); return; }
        try {
            const res = await getData(`subcategories/list?limit=1000&category_id=${categoryId}`, null, authToken());
            setSubcategories(Array.isArray(res?.subcategories) ? res.subcategories : []);
        } catch { setSubcategories([]); }
    }, []);

    useEffect(() => {
        fetchCategories();
        setLoading(true);
        getData(`product/${id}`, null, authToken())
            .then((res) => {
                const p = res?.product;
                if (!p?._id) throw new Error('missing');
                const next = formFromProduct(p);
                setForm(next);
                fetchSubcategories(next.category_id);
            })
            .catch(() => setLoadError('This product could not be loaded. It may have been removed, or it is not yours.'))
            .finally(() => setLoading(false));
    }, [id, fetchCategories, fetchSubcategories]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((f) => applyFormChange(f, name, value));
        if (name === 'category_id') fetchSubcategories(value);
    };

    const handleUpdate = async () => {
        setSubmitting(true);
        setServerError('');
        try {
            await putData(`product/update/${id}`, buildProductFormData(form, { isEdit: true }), authToken());
            navigate('/products', { state: { notice: `“${form.title.trim()}” was updated.` } });
        } catch (error) {
            setServerError(error?.message || 'Could not update the product.');
            window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <Box sx={{ p: 3 }}><Skeleton variant="rounded" height={90} sx={{ borderRadius: '14px', mb: 2 }} /><Skeleton variant="rounded" height={420} sx={{ borderRadius: '14px' }} /></Box>;
    if (loadError) {
        return (
            <Box sx={{ p: 3, maxWidth: 560, mx: 'auto' }}>
                <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>
                <Button onClick={() => navigate('/products')} sx={yellowBtn}>Back to products</Button>
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 1.5, md: 3 }, fontFamily: FONT }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <IconButton onClick={() => navigate('/products')} sx={{ background: '#f1f5f9' }}><MdArrowBack size={18} /></IconButton>
                <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 24, color: '#0f172a' }}>Edit product</Typography>
                    <Typography sx={{ fontFamily: FONT, fontSize: 13.5, color: '#64748b' }}>{form.productCode ? `${form.productCode} · ` : ''}{form.title}</Typography>
                </Box>
                <StatusChip status={form.status} />
            </Box>
            {form.status === 'rejected' && <Alert severity="warning" sx={{ mb: 2.5 }}>This product was rejected. Fix what is wrong and save it again for another look.</Alert>}
            {form.status === 'pending' && <Alert severity="info" sx={{ mb: 2.5 }}>This product is waiting for approval. You can still change it.</Alert>}
            <ProductForm
                form={form}
                onFormChange={handleChange}
                onCancel={() => navigate('/products')}
                onSubmit={handleUpdate}
                categories={categories}
                subcategories={subcategories}
                isEdit
                fetchCategories={fetchCategories}
                fetchSubcategories={fetchSubcategories}
                submitting={submitting}
                serverError={serverError}
            />
        </Box>
    );
};

export default EditProduct;
